import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { LinkHarmReportsModalProps } from './linkHarmReportsModal.types';
import styles from './linkHarmReportsModal.module.css';
import ModalComponent from '../modal/modal';
import {
  GetReportsByFilterRequest,
  HarmReport,
  ReportStatus,
} from '@inplay/models';
import { ReportService } from '../../service/reportService';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { Trans, useTranslation } from 'react-i18next';
import Loading from '../../assets/loading/loading';
import SearchIcon from '../../assets/search.png';
import Button from '../button/button';
import Countdown from '../countdown/countdown';
import {
  getTranslatedTextValues,
  renderHarmObservedDialogContent,
} from '../../pages/harmReports/harmReportesHelper';
import { HarmReportDetails } from '../../pages/harmReports/harmReportDetails';

const LinkHarmReportsModal: FC<LinkHarmReportsModalProps> = (props) => {
  const { t } = useTranslation();
  const { selectedVenueBranch } = useOrganizationContext();
  const [harmReports, setHarmReports] = useState<HarmReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>(props.selectedIds ?? []);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewingReport, setViewingReport] = useState<HarmReport | null>(null);
  const reportService = useMemo(() => new ReportService(), []);

  const fetchHarmReports = useCallback(async () => {
    if (!selectedVenueBranch) return;
    setLoading(true);
    try {
      const query: GetReportsByFilterRequest & { linked?: boolean } = {
        venueBranchId: selectedVenueBranch._id,
        currentDate: new Date(),
        playerId: props.playerId,
        status: [ReportStatus.ACTIVE],
        linked: false,
      };
      const response = await reportService.getHarmReports(query);
      const reports: HarmReport[] = response.data?.harmReports ?? [];
      setHarmReports(reports);
    } catch (error) {
      console.error('[LinkHarmReportsModal] Error fetching harm reports', error);
    } finally {
      setLoading(false);
    }
  }, [selectedVenueBranch, props.playerId]);

  useEffect(() => {
    fetchHarmReports();
  }, [fetchHarmReports]);

  const filteredReports: HarmReport[] = useMemo(() => {
    if (!searchQuery) return harmReports;
    const q: string = searchQuery.toLowerCase();
    return harmReports.filter(
      (r) =>
        r.reportRefNo?.toLowerCase().includes(q) ||
        r.playerProfile?.id?.toLowerCase().includes(q) ||
        r.createdBy?.firstName?.toLowerCase().includes(q) ||
        r.createdBy?.lastName?.toLowerCase().includes(q) ||
        r.reviewedBy?.firstName?.toLowerCase().includes(q) ||
        r.reviewedBy?.lastName?.toLowerCase().includes(q),
    );
  }, [harmReports, searchQuery]);

  const toggleSelect = (reportId: string): void => {
    setSelectedIds((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId],
    );
  };

  const toggleSelectAll = (): void => {
    if (selectedIds.length === filteredReports.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(
        filteredReports
          .map((r) => r.originalReportId)
          .filter((id): id is string => Boolean(id)),
      );
    }
  };

  const getHarmObservedCell = (report: HarmReport): JSX.Element => {
    const { lengthOfPlay, money, behaviour, other } =
      report.signsOfGamblingHarm;

    const translatedLengthOfPlay: string[] = getTranslatedTextValues(
      lengthOfPlay,
      t,
    );
    const translatedMoney: string[] = getTranslatedTextValues(money, t);
    const translatedBehaviour: string[] = getTranslatedTextValues(
      behaviour,
      t,
    );
    const translatedOther: string[] = getTranslatedTextValues(other, t);

    const allItems: string[] = [
      ...translatedLengthOfPlay,
      ...translatedMoney,
      ...translatedBehaviour,
      ...translatedOther,
    ];
    const firstItem: string = allItems[0] || '';
    const totalCount: number =
      lengthOfPlay.length + money.length + behaviour.length + other.length;
    const maxLength: number = 20;
    const displayText: string =
      firstItem.length > maxLength
        ? firstItem.slice(0, maxLength) + '…'
        : firstItem;

    return (
      <div className="relative group inline-block">
        <span className="underline cursor-pointer">
          {displayText} ({totalCount})
        </span>
        {renderHarmObservedDialogContent({
          lengthOfPlay: translatedLengthOfPlay,
          money: translatedMoney,
          behaviour: translatedBehaviour,
          other: translatedOther,
        })}
      </div>
    );
  };


  return (
    <div className={styles.linkHarmReportsOverlay}>
      <ModalComponent
        open
        onToggle={props.onClose}
        title={<span className="text-[22px]">{t('linkHarmReports.title')}</span>}
        isShowClose
        centered
        noHeaderBorder
        dialogClassName="w-[calc(100vw-60px)] h-[calc(100vh-60px)] max-w-none"
        footerHidden
      >
        <div className="flex flex-col" style={{ height: "calc(100vh - 160px)" }}>
          <p className="text-[14px] text-gray-600 mb-[30px]">
            <Trans i18nKey="linkHarmReports.subtitle" />
          </p>

          {/* Search */}
          <div className="mb-4 relative w-[200px]">
            <img
              src={SearchIcon}
              alt=""
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50"
            />
            <input
              type="text"
              placeholder={t('iconLabels.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-full text-sm bg-[#F9F9FC] focus:outline-none focus:border-[#561BDC]"
            />
          </div>

          {/* Table */}
          <div className="flex-grow overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loading />
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                {t('linkHarmReports.noReports')}
              </div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-y text-left text-gray-500 font-bold">
                    <th className="p-3 w-10">
                      <input
                        type="checkbox" style={{ accentColor: "#561BDC" }}
                        checked={
                          selectedIds.length === filteredReports.length &&
                          filteredReports.length > 0
                        }
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="p-3">{t('linkHarmReports.reportNo')}</th>
                    <th className="p-3">{t('linkHarmReports.expiresIn')}</th>
                    <th className="p-3">{t('linkHarmReports.playerNo')}</th>
                    <th className="p-3">{t('linkHarmReports.harmObserved')}</th>
                    <th className="p-3">
                      {t('linkHarmReports.harmInstance')}
                    </th>
                    <th className="p-3">{t('linkHarmReports.createdBy')}</th>
                    <th className="p-3">{t('linkHarmReports.reviewedBy')}</th>
                    <th className="p-3">{t('linkHarmReports.viewReport')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((report: HarmReport) => (
                    <tr
                      key={report.originalReportId}
                      className="border-b hover:bg-gray-50 font-normal"
                    >
                      <td className="p-3">
                        <input
                          type="checkbox" style={{ accentColor: "#561BDC" }}
                          checked={selectedIds.includes(
                            report.originalReportId ?? '',
                          )}
                          onChange={() =>
                            toggleSelect(report.originalReportId ?? '')
                          }
                        />
                      </td>
                      <td className="p-3">{report.reportRefNo}</td>
                      <td className="p-3">
                        {report.expiresAt
                          ? <Countdown expiresAt={report.expiresAt} className="text-red-500 text-xs font-medium" />
                          : '-'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {report.playerProfile?.faceImage && (
                            <img
                              src={report.playerProfile.faceImage}
                              alt=""
                              className="w-[40px] h-[40px] rounded-full object-cover"
                            />
                          )}
                          <span>
                            {report.playerProfile?.id?.slice(0, 5) ?? '-'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">{getHarmObservedCell(report)}</td>
                      <td className="p-3">
                        {report.harmInstanceCountForPlayer ?? '-'}
                      </td>
                      <td className="p-3">
                        {report.createdBy
                          ? `${report.createdBy.firstName} ${report.createdBy.lastName}`
                          : 'Autogenerated'}
                      </td>
                      <td className="p-3">
                        {report.reviewedBy
                          ? `${report.reviewedBy.firstName} ${report.reviewedBy.lastName}`
                          : 'N/A'}
                      </td>
                      <td className="p-3">
                        <span
                          className="text-[#561BDC] cursor-pointer underline"
                          onClick={() => setViewingReport(report)}
                        >
                          {t('linkHarmReports.view')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex justify-center items-center gap-4 mt-auto shrink-0 mx-[-40px]"
            style={{ height: "90px", background: "#FFFFFF", boxShadow: "0px -4px 20px #4D36CE0F", borderRadius: "0px 0px 16px 16px" }}
          >
            <Button
              onClick={props.onSkip}
              className="rounded-full border border-[#A3A7FF] bg-white text-[#561BDC]"
              style={{ width: "120px", height: "50px" }}
            >
              <Trans i18nKey="linkHarmReports.skip" />
            </Button>
            <Button
              onClick={() => {
                const refNos = filteredReports.filter(
                  (r) => selectedIds
                    .includes(r.originalReportId ?? "")).map((r) => r.reportRefNo);
                props.onLink(selectedIds, refNos);
              }}
              disabled={selectedIds.length === 0}
              className="rounded-full text-white disabled:bg-[#D8D9E5] disabled:text-white bg-[#561BDC]"
              style={{ width: "300px", height: "50px" }}
            >
              <Trans i18nKey="linkHarmReports.linkReports" />
            </Button>
          </div>
        </div>
      </ModalComponent>

      {viewingReport && (
        <HarmReportDetails
          harmReport={viewingReport}
          closeHarmReportDetails={() => setViewingReport(null)}
          handleReviewAndSignClick={() => setViewingReport(null)}
        />
      )}
    </div>
  );
};

export default LinkHarmReportsModal;
